import {Component, EventEmitter, Input, Output, ViewChild, ChangeDetectionStrategy} from "@angular/core";
import {AbstractControl} from "@angular/forms";
import {TargetScopes} from "app/aws-ssm/automation-document/document-execution-dialog/document-execution-dialog.component";
import {BaseApplicationComponent} from "app/services/domain/base.service";
import * as Models from "app/services/proxy/model/models";
import {ParameterInputType} from "app/shared/dynamic-parameters/dynamic-parameters-base.component";

import {Lookup} from "framework/services/utils.service";
import {ControlOption} from "framework/ui/control-option";
import {StandardFormComponent} from "framework/ui/overlays/standard-form.component";

@Component({
               selector   : "cs-input-parameters",
               templateUrl: "./input-parameters.component.html",
               changeDetection: ChangeDetectionStrategy.Eager,
               standalone : false
           })
export class InputParametersComponent extends BaseApplicationComponent
{
    public readonly ParameterInputType = ParameterInputType;

    @Input() executionType: ExecutionType;
    @Input() paramValue: string;
    @Input() parameters: Models.AwsSsmParameter[];
    @Input() targetValue: TargetScopes;

    // currentValue/ currentModelValueChange -> model value of the form fields
    @Input() currentModelValue: string;
    @Output() currentModelValueChange = new EventEmitter<string>();

    @Output() valueChange     = new EventEmitter<string>();
    @Output() formValidChange = new EventEmitter<boolean>();
    @Output() inputParamValid = new EventEmitter<boolean>();
    @ViewChild(StandardFormComponent) form: StandardFormComponent;

    private m_parameterExts: AwsSsmParameterExtended[];
    get parameterExts(): AwsSsmParameterExtended[]
    {
        return this.m_parameterExts;
    }

    ngOnInit(): void
    {
        const value          = JSON.parse(this.currentModelValue || "{}");
        this.m_parameterExts = this.parameters.map(param => new AwsSsmParameterExtended(param, this.executionType, this.paramValue, this.targetValue, value?.[param.name]));
        if (this.currentModelValue)
        {
            this.valueChangeEmit();
        }
    }

    public checkInputValid(isEmpty: boolean,
                           paramName: string): void
    {
        if (this.executionType == ExecutionType.RateControl && paramName === this.paramValue && this.targetValue == TargetScopes.ParameterValues)
        {
            this.inputParamValid.emit(!isEmpty);
        }
    }

    public trimArray(arrayValue: string[]): string[]
    {
        return arrayValue.map(item => item.trim());
    }

    public get formControls(): Lookup<AbstractControl>
    {
        return this.form?.form?.form?.controls;
    }

    public invalidInput(paramName: string): void
    {
        this.formControls[paramName].setErrors({"incorrect": true});
        this.inputParamValid.emit(false);
    }

    private validateArrayOfObjects(value: any): void
    {
        if (!Array.isArray(value))
        {
            throw new TypeError("Value must be an array.");
        }

        if (!value.every(item => typeof item === "object"))
        {
            throw new TypeError("All elements in the array must be objects.");
        }
    }

    public getSimpleTypeValue(param: Models.AwsSsmParameter,
                              paramValue: string): string[]
    {
        try
        {
            this.formControls[param.name].setErrors(null);
            switch (param.type)
            {
                case Models.AwsSsmDocumentParameterType.Boolean:
                case Models.AwsSsmDocumentParameterType.StringMap:
                    // Parsing to validate boolean/object string
                    JSON.parse(paramValue);
                    return [paramValue];

                case Models.AwsSsmDocumentParameterType.Integer:
                    return [paramValue.toString()];

                case Models.AwsSsmDocumentParameterType.MapList:
                    const parsedVal = JSON.parse(paramValue);
                    this.validateArrayOfObjects(parsedVal);
                    return parsedVal;

                case Models.AwsSsmDocumentParameterType.StringList:
                    return this.trimArray(paramValue.split(","));

                default:
                    return [paramValue];
            }
        }
        catch (e)
        {
            return null;
        }
    }

    public getRateControlValue(param: Models.AwsSsmParameter,
                               paramValue: string): string[]
    {
        try
        {
            this.formControls[param.name].setErrors(null);
            switch (param.type)
            {
                case Models.AwsSsmDocumentParameterType.Boolean:
                    // Parsing to validate boolean string
                    JSON.parse(paramValue);
                    return this.trimArray(paramValue.split(","));

                case Models.AwsSsmDocumentParameterType.MapList:
                    const parsedVal = JSON.parse(paramValue);
                    this.validateArrayOfObjects(parsedVal);
                    return parsedVal;

                case Models.AwsSsmDocumentParameterType.Integer:
                    return [paramValue.toString()];

                case Models.AwsSsmDocumentParameterType.StringMap:
                    // Parsing to validate object
                    JSON.parse(paramValue);
                    return this.trimArray(paramValue.match(/{.*?}/g));

                default:
                    return this.trimArray(paramValue.split(","));
            }
        }
        catch (e)
        {
            return null;
        }
    }

    public valueChangeEmit(): void
    {
        const currentModelValue: Lookup<string>     = {};
        const formattedModelValue: Lookup<string[]> = {};

        this.inputParamValid.emit(true);
        for (let param of this.parameterExts)
        {
            const paramValue = param.value;
            let isValueEmpty = !paramValue;
            this.checkInputValid(isValueEmpty, param.model.name);
            if (paramValue)
            {
                currentModelValue[param.model.name] = paramValue;
                let formattedValue: string[];
                if (this.executionType === ExecutionType.RateControl)
                {
                    formattedValue = this.getRateControlValue(param.model, paramValue);
                }
                else
                {
                    formattedValue = this.getSimpleTypeValue(param.model, paramValue);
                }
                formattedModelValue[param.model.name] = formattedValue;
                if (formattedValue == null)
                {
                    this.invalidInput(param.model.name);
                }
            }
        }
        this.valueChange.emit(JSON.stringify(formattedModelValue));
        this.currentModelValueChange.emit(JSON.stringify(currentModelValue));
        this.formValidChange.emit(this.form?.form.valid);
    }
}

export class AwsSsmParameterExtended
{
    public readonly options: ControlOption<string>[] = [];
    public readonly placeholderDescription: string;
    private m_value: string;

    constructor(public readonly model: Models.AwsSsmParameter,
                public readonly executionType: ExecutionType,
                public readonly targetParameter: string,
                public readonly targetValue: TargetScopes,
                private m_initialValue?: any)
    {
        if (model?.allowed_values?.length > 0)
        {
            this.options                = model.allowed_values.map(param => new ControlOption<string>(param, param));
            this.placeholderDescription = "Select " + model.name;
        }
        this.m_value       = this.m_initialValue;
        const defaultValue = model?.default_value;
        if (!this.m_value && defaultValue)
        {
            if (model.type === Models.AwsSsmDocumentParameterType.StringList)
            {
                try
                {
                    this.m_value = JSON.parse(defaultValue)
                                       .join(",");
                }
                catch
                {
                    this.m_value = defaultValue;
                }
            }
            else
            {
                this.m_value = defaultValue;
            }
        }
    }

    public getInputType()
    {
        const param = this.model;
        switch (param.type)
        {
            case Models.AwsSsmDocumentParameterType.StringList:
            case Models.AwsSsmDocumentParameterType.MapList:
                return ParameterInputType.Textarea;

            case Models.AwsSsmDocumentParameterType.Integer:
                return ParameterInputType.Number;

            default:
                if (param.type === Models.AwsSsmDocumentParameterType.String && param?.allowed_values?.length > 0)
                {
                    return ParameterInputType.Dropdown;
                }
                return ParameterInputType.String;
        }
    }

    set value(formValue: string)
    {
        this.m_value = formValue;
    }

    get value(): string
    {
        return this.m_value;
    }

    get isDisabled(): boolean
    {
        return this.executionType == ExecutionType.RateControl && this.targetParameter === this.model.name && this.targetValue !== TargetScopes.ParameterValues;
    }

    get isRequired(): boolean
    {
        return this.executionType == ExecutionType.RateControl && this.targetParameter === this.model.name && this.targetValue === TargetScopes.ParameterValues;
    }
}

export enum ExecutionType
{
    RateControl = "RateControl",
    Simple      = "Simple",
    Command     = "Command"
}

export enum CommandTargetType
{
    Tags          = "Tags",
    Instance      = "Instance",
    ResourceGroup = "ResourceGroup"
}
